import React, { useState, useEffect, useCallback } from 'react';
import { FaShieldAlt, FaSearch, FaSync } from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, Button, Field, inputClass, Alert, Loading, Table,
  EmptyState, formatDateTime,
} from './attendance/ui';

const ACTION_TONE = (action) => {
  if (action.includes('delete') || action.includes('reject') || action.includes('failed')) {
    return 'bg-red-100 text-red-700';
  }
  if (action.includes('create') || action.includes('approve') || action === 'login') {
    return 'bg-green-100 text-green-700';
  }
  if (action.includes('update') || action.includes('sync') || action.includes('export')) {
    return 'bg-blue-100 text-blue-700';
  }
  return 'bg-gray-100 text-gray-600';
};

/** Security audit trail: who did what, when and from where. */
const AuditTrail = () => {
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (action) params.action = action;
      const { data } = await api.get('audit-logs/', { params });
      setEntries(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the audit trail.'));
    } finally {
      setLoading(false);
    }
  }, [search, action]);

  useEffect(() => { load(); }, [load]);

  const actions = [...new Set(entries.map((entry) => entry.action))].sort();

  return (
    <PageShell
      title="Audit Trail"
      subtitle="Every recorded administrative and attendance action"
      actions={<Button variant="secondary" onClick={load}><FaSync /> Refresh</Button>}
    >
      <Alert>{error}</Alert>

      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Search">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input value={search} onChange={(event) => setSearch(event.target.value)}
                placeholder="User, entity, description…" className={`${inputClass} pl-9`} />
            </div>
          </Field>
          <Field label="Action">
            <select value={action} onChange={(event) => setAction(event.target.value)} className={inputClass}>
              <option value="">All actions</option>
              {actions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card title={`Recorded events (${entries.length})`}>
        {loading ? <Loading /> : entries.length === 0 ? (
          <EmptyState icon={<FaShieldAlt />} title="No audit entries"
            hint="Sign-ins, staff changes, leave decisions and exports are recorded here." />
        ) : (
          <Table
            columns={['When', 'User', 'Action', 'Entity', 'Description', 'IP address']}
            rows={entries}
            renderRow={(entry) => (
              <tr key={entry.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 whitespace-nowrap text-xs text-gray-600">
                  {formatDateTime(entry.createdAt)}
                </td>
                <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap">
                  {entry.username || 'system'}
                </td>
                <td className="py-3 pr-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ACTION_TONE(entry.action)}`}>
                    {entry.action}
                  </span>
                </td>
                <td className="py-3 pr-4 whitespace-nowrap text-gray-600">
                  {entry.entity || '—'}{entry.entity_id ? ` #${entry.entity_id}` : ''}
                </td>
                <td className="py-3 pr-4 text-gray-600">{entry.description || '—'}</td>
                <td className="py-3 pr-4 font-mono text-xs text-gray-500">{entry.ipAddress || '—'}</td>
              </tr>
            )}
          />
        )}
      </Card>
    </PageShell>
  );
};

export default AuditTrail;
